import { Tag as ChakraTag } from '@chakra-ui/react'
import * as React from 'react'

export interface TagProps extends ChakraTag.RootProps {
  startElement?: React.ReactNode
  endElement?: React.ReactNode
  onClose?: VoidFunction
  closable?: boolean
}

export const Tag = React.forwardRef<HTMLSpanElement, TagProps>(
  function Tag(props, ref) {
    const {
      variant,
      colorPalette,
      size,
      startElement,
      endElement,
      onClose,
      closable = !!onClose,
      children,
      ...rest
    } = props

    let bgColor: string | undefined = undefined
    let textColor: string | undefined = undefined
    let border: string | undefined = undefined
    let hoverBg: string | undefined = undefined
    let activeBg: string | undefined = undefined
    let overlayBgColor: string | undefined = undefined
    switch (variant) {
      case 'solid':
        bgColor = 'colorPalette.500'
        hoverBg = 'colorPalette.500/92'
        activeBg = 'colorPalette.500/84'
        textColor = 'white'
        break
      case 'subtle':
        bgColor =
          colorPalette === 'gray' ? 'colorPalette.200' : 'colorPalette.100'
        overlayBgColor = '{colors.colorPalette.500/8}'
        hoverBg =
          colorPalette === 'gray'
            ? 'linear-gradient(0deg, var(--overlay-bg-color), var(--overlay-bg-color)), var(--chakra-colors-color-palette-200);'
            : 'linear-gradient(0deg, var(--overlay-bg-color), var(--overlay-bg-color)), var(--chakra-colors-color-palette-100);'
        activeBg =
          colorPalette === 'gray'
            ? 'linear-gradient(0deg, var(--overlay-bg-color), var(--overlay-bg-color)), linear-gradient(0deg, var(--overlay-bg-color), var(--overlay-bg-color)), var(--chakra-colors-color-palette-200)'
            : 'linear-gradient(0deg, var(--overlay-bg-color), var(--overlay-bg-color)), linear-gradient(0deg, var(--overlay-bg-color), var(--overlay-bg-color)), var(--chakra-colors-color-palette-100)'
        textColor = 'colorPalette.800'
        break
      case 'outline':
        border = '1px solid colorPalette.500'
        overlayBgColor = '{colors.colorPalette.500/8}'
        hoverBg =
          'linear-gradient(0deg, var(--overlay-bg-color), var(--overlay-bg-color));'
        activeBg =
          'linear-gradient(0deg, var(--overlay-bg-color), var(--overlay-bg-color)), linear-gradient(0deg, var(--overlay-bg-color), var(--overlay-bg-color))'
        textColor = 'colorPalette.600'
        break
      default:
        break
    }

    let height = '24px'
    let fontSize = '14px'
    let paddingX = 2
    switch (size) {
      case 'sm':
        height = '20px'
        fontSize = '12px'
        break
      case 'md':
        break
      case 'lg':
        height = '32px'
        fontSize = '16px'
        paddingX = 3
        break
      default:
        break
    }
    return (
      <ChakraTag.Root
        ref={ref}
        variant={variant}
        colorPalette={colorPalette}
        bgColor={bgColor}
        color={textColor}
        border={border}
        borderRadius="base"
        h={height}
        fontSize={fontSize}
        px={paddingX}
        css={{
          '--overlay-bg-color': overlayBgColor,
          '&:hover': {
            bg: hoverBg,
          },
          '&:active': {
            bg: activeBg,
          },
        }}
        {...rest}
      >
        {startElement && (
          <ChakraTag.StartElement>{startElement}</ChakraTag.StartElement>
        )}
        <ChakraTag.Label>{children}</ChakraTag.Label>
        {endElement && (
          <ChakraTag.EndElement>{endElement}</ChakraTag.EndElement>
        )}
        {closable && (
          <ChakraTag.EndElement>
            <ChakraTag.CloseTrigger onClick={onClose} />
          </ChakraTag.EndElement>
        )}
      </ChakraTag.Root>
    )
  },
)
